/** Features with no creatures in them: brick rooms, wall lines, boulder shelves, diamond caches. */

import type { Rng } from '../../sim/rng';
import type { Plan } from './plan';
import { between, chance } from './plan';
import type { Rect, Sketch } from './sketch';

/** A brick room with a few diamonds and one or two doorways, some plugged by a boulder to push in. */
export function room(sk: Sketch, rng: Rng, plan: Plan): boolean {
  const ow = plan.bonus ? between(rng, 5, 6) : between(rng, 6, 9);
  const oh = plan.bonus ? 4 : between(rng, 4, 6);
  const spot = sk.findSpot(rng, ow + 2, oh + 2);
  if (!spot) return false;
  sk.claim(spot);
  const r = { x: spot.x + 1, y: spot.y + 1, w: ow, h: oh };
  sk.outline(r, 'w');
  for (let y = r.y + 1; y < r.y + r.h - 1; y++) {
    for (let x = r.x + 1; x < r.x + r.w - 1; x++) {
      const roll = rng.int(100);
      sk.set(x, y, roll < 32 ? 'd' : roll < 42 ? 'r' : roll < 52 ? ' ' : '.');
    }
  }
  const west = rng.oneIn(2);
  doorway(sk, rng, r, west);
  if (ow >= 8 && chance(rng, 40)) doorway(sk, rng, r, !west);
  return true;
}

/**
 * A doorway in a side wall. Never in the roof or floor: a boulder can drop into those and shut
 * Digby in, while nothing can fall or roll into a side doorway (brick above and below it).
 */
function doorway(sk: Sketch, rng: Rng, r: Rect, west: boolean): void {
  const x = west ? r.x : r.x + r.w - 1;
  const inner = west ? x + 1 : x - 1;
  const outer = west ? x - 1 : x + 1;
  const y = r.y + 1 + rng.int(r.h - 2);
  sk.set(outer, y, '.');
  if (!rng.oneIn(2)) {
    sk.set(x, y, '.');
    return;
  }
  // The plug can be pushed in: space behind it, dirt under that so it cannot roll in on its
  // own, and nothing above that could drop into the space first.
  sk.set(x, y, 'r');
  sk.set(inner, y, ' ');
  sk.set(inner, y - 1, y - 1 > r.y ? '.' : 'w');
  if (y + 1 < r.y + r.h - 1) sk.set(inner, y + 1, '.');
  sk.set(outer, y + 1, '.');
}

/** A long brick wall, mostly horizontal, broken by one to three dirt gaps. */
export function wallLine(sk: Sketch, rng: Rng, plan: Plan): boolean {
  const flat = chance(rng, 70);
  const [lo, hi] = flat ? (plan.bonus ? [6, 10] : [10, 24]) : plan.bonus ? [4, 6] : [5, 12];
  const len = between(rng, lo, hi);
  const spot = flat ? sk.findSpot(rng, len, 3) : sk.findSpot(rng, 3, len);
  if (!spot) return false;
  sk.claim(spot);
  const put = (i: number, ch: string): void => {
    if (flat) sk.set(spot.x + i, spot.y + 1, ch);
    else sk.set(spot.x + 1, spot.y + i, ch);
  };
  for (let i = 0; i < len; i++) put(i, 'w');
  const gaps = 1 + rng.int(len > 14 ? 3 : 2);
  for (let g = 0; g < gaps; g++) put(1 + rng.int(len - 2), '.');
  return true;
}

/**
 * A brick shelf with a row of boulders on it and diamonds tucked under it. Dig beside either
 * end and the boulders roll off the brick.
 */
export function shelf(sk: Sketch, rng: Rng, plan: Plan): boolean {
  const len = plan.bonus ? between(rng, 3, 6) : between(rng, 4, 9);
  const spot = sk.findSpot(rng, len + 2, 4);
  if (!spot) return false;
  sk.claim(spot);
  sk.fillRect(spot, '.');
  for (let i = 0; i < len; i++) {
    const x = spot.x + 1 + i;
    sk.set(x, spot.y + 1, chance(rng, 80) ? 'r' : 'd');
    sk.set(x, spot.y + 2, 'w');
    sk.set(x, spot.y + 3, chance(rng, 45) ? 'd' : '.');
  }
  return true;
}

/** A buried hoard of diamonds: open, capped with boulders, or in a brick cup. */
export function cache(sk: Sketch, rng: Rng, plan: Plan): boolean {
  const cw = plan.bonus ? between(rng, 2, 4) : between(rng, 3, 5);
  const ch = between(rng, 2, 3);
  const spot = sk.findSpot(rng, cw + 2, ch + 3);
  if (!spot) return false;
  sk.claim(spot);
  sk.fillRect(spot, '.');
  const x0 = spot.x + 1;
  const y0 = spot.y + 2;
  for (let y = y0; y < y0 + ch; y++) for (let x = x0; x < x0 + cw; x++) sk.set(x, y, chance(rng, 75) ? 'd' : '.');
  const style = rng.int(3);
  if (style === 1) {
    for (let x = x0; x < x0 + cw; x++) sk.set(x, y0 - 1, 'r');
  } else if (style === 2) {
    for (let y = y0; y <= y0 + ch; y++) {
      sk.set(x0 - 1, y, 'w');
      sk.set(x0 + cw, y, 'w');
    }
    for (let x = x0; x < x0 + cw; x++) sk.set(x, y0 + ch, 'w');
  }
  return true;
}
